<template>
  <div class="product-mapping-tab">
    <div class="product-mapping-tab__section">
      <div class="flex flex-col gap-16">
        <div>
          <h3 class="section-title">Choose your Salesforce object</h3>
          <p class="section-description">
            Choose the object that holds your account product data
          </p>
        </div>
        <BittsCallout
          v-if="isEditable"
          size="x-small"
          type="warning"
          title="This object cannot be changed after saving"
          subtitle="Pick the Salesforce object that holds your account-level product data. Once you click Save, this object can't be edited."
        />
        <BittsDivider class="my-0" />
        <div class="product-mapping-tab__headers">
          <span class="product-mapping-tab__header-label">CRM fields</span>
          <span aria-hidden="true" />
          <TextWithTooltip
            text="Crossbeam product fields"
            text-classes="text-neutral-text-weak text-sm font-bold"
            tooltip-text="These are the Crossbeam fields your CRM fields will be mapped to."
          />
        </div>
      </div>
      <div class="product-mapping-tab__rows">
        <div
          v-for="row in PRODUCT_MAPPING_ROWS"
          :key="row.mdmProperty"
          class="product-mapping-tab__row"
        >
          <BittsSelect
            class="product-mapping-tab__row-select"
            placeholder="Select your CRM field"
            :disabled="!isEditable || rowGate.isDisabled"
            :model-value="mappedFieldOptions(row.mdmProperty)[0]?.value"
            :options="mappedFieldOptions(row.mdmProperty)"
          />
          <FontAwesomeIcon
            :icon="['fak', 'arrow-right']"
            class="product-mapping-tab__row-arrow"
          />
          <BittsSelect
            class="product-mapping-tab__row-select"
            disabled
            :model-value="row.mdmProperty"
            :options="[{ value: row.mdmProperty, label: row.label }]"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { BittsCallout, BittsDivider, BittsSelect } from '@crossbeam/bitts';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { computed, ref } from 'vue';

import TextWithTooltip from '@/components/performance-dashboard/TextWithTooltip.vue';

import { MDM_PROPERTIES, MDM_TYPES, MdmPropertyValue } from '@/constants/mdm';
import type { SourceField } from '@/types/sources';

import { useAccountProductSource } from '../composables/useAccountProductSource';

type Props = {
  feedId: number;
};

const props = defineProps<Props>();

const ACCOUNT_PRODUCT_PROPERTIES = MDM_PROPERTIES[MDM_TYPES.ACCOUNT_PRODUCT];

type ProductMappingRow = {
  label: string;
  mdmProperty: MdmPropertyValue<typeof MDM_TYPES.ACCOUNT_PRODUCT>;
};

const PRODUCT_MAPPING_ROWS: ProductMappingRow[] = [
  {
    label: 'Account ID',
    mdmProperty: ACCOUNT_PRODUCT_PROPERTIES.COMPANY_ID,
  },
  {
    label: 'Product Name',
    mdmProperty: ACCOUNT_PRODUCT_PROPERTIES.NAME,
  },
  {
    label: 'Status',
    mdmProperty: ACCOUNT_PRODUCT_PROPERTIES.STATUS,
  },
];

const { accountProductSource, fieldsByMdmProperty } = useAccountProductSource(
  () => props.feedId,
);

const isEditable = computed(() => !accountProductSource.value);

const mappedFields = computed<Record<string, SourceField>>(() =>
  isEditable.value ? {} : fieldsByMdmProperty.value,
);

function mappedFieldOptions(mdmProperty: ProductMappingRow['mdmProperty']) {
  const field = mappedFields.value[mdmProperty];
  return field ? [{ value: field.column, label: field.display_name }] : [];
}

type FieldsStatus = 'idle' | 'loading' | 'loaded' | 'failed';

// TODO: driven by the object-catalog select and the fields fetch once that
// ticket lands; nothing sets these outside tests yet, so the rows stay
// disabled in the "nothing picked" state.
const selectedObjectApiName = ref<string | undefined>(undefined);
const fieldsStatus = ref<FieldsStatus>('idle');

const rowGate = computed(() => {
  if (!selectedObjectApiName.value) {
    return { isDisabled: true, reason: 'Pick an object to map its fields' };
  }
  if (fieldsStatus.value === 'loading') {
    return { isDisabled: true, reason: 'Loading fields…' };
  }
  if (fieldsStatus.value === 'failed') {
    return { isDisabled: true, reason: 'Could not load fields. Try again.' };
  }
  return { isDisabled: false, reason: undefined };
});

// TODO: hardcoded false until the rows are interactive; should reflect real
// unsaved selections once the CRM-field selects are wired up.
const hasPendingChanges = computed(() => false);
const isSaving = ref(false);

// TODO: no-op until save is wired to POST /v0.1/feeds/{id}/sources/account-product.
function handleSaveAll() {
  return Promise.resolve();
}

defineExpose({
  hasPendingChanges,
  isSaving,
  handleSaveAll,
});
</script>

<style lang="pcss" scoped>
@reference "../../../../../css/theme.css";
.product-mapping-tab {
  @apply flex flex-col gap-16;
}

.product-mapping-tab__headers {
  @apply grid grid-cols-[1fr_24px_1fr] gap-16 mb-8;
}

.section-title {
  @apply text-neutral-text-strong text-m font-bold;
}

.section-description {
  @apply text-neutral-text;
}

.product-mapping-tab__header-label {
  @apply text-neutral-text-weak text-sm font-bold;
}

.product-mapping-tab__rows {
  @apply flex flex-col gap-8;
}

.product-mapping-tab__row {
  @apply grid grid-cols-[1fr_24px_1fr] items-center gap-16;
}

.product-mapping-tab__row-arrow {
  @apply text-neutral-accent justify-self-center;
}
</style>
